import React from 'react';
import { ArrowDownRightIcon } from 'lucide-react';
import type { BundleId } from '../../data/pricing';
import { useLanguage } from '../../contexts/LanguageContext';
import { bundleName, formatMoney, getBundle, priceBundle } from '../../utils/pricing';
import { goToShop } from '../../utils/shopIntent';
import { track } from '../../utils/analytics';

/** Three entry points shown under the hero; each pre-selects its system in the shop section. */
const PICKS: BundleId[] = ['sys1', 'sys8', 'sys24'];

/** X1-style "pick your path" row under the hero: each choice jumps to its product in the shop section. */
export function QuickPick() {
  const {
    lang
  } = useLanguage();
  const es = lang === 'es';
  const m = (n: number) => formatMoney(n, lang);
  return <section aria-labelledby="quickpick-title" className="border-b mf-hairline bg-deep" data-id="element-354" data-mp="3ezgdqyldzaia">
      <div className="mx-auto max-w-site px-5 py-10 md:px-8 md:py-14" data-id="element-355" data-mp="2r9m04et8thwz">
        <h2 id="quickpick-title" className="text-center font-display text-3xl font-semibold uppercase tracking-[0.04em] md:text-4xl" data-id="element-356" data-mp="2mbubbtnwabx0">
          {es ? 'Elige tu sistema M1 PRO' : 'Choose your M1 PRO system'}
        </h2>

        <div className="mt-8 grid gap-4 md:grid-cols-3" data-id="element-357" data-mp="11b10cz3aed1v">
          {PICKS.map(id => {
          const b = getBundle(id);
          const p = priceBundle(b);
          const featured = b.badge === 'mostPopular';
          const kicker = b.badge === 'mostPopular' ? es ? 'Más popular' : 'Most popular' : b.badge === 'bestValue' ? es ? 'Mejor valor' : 'Best value' : es ? b.taglineEs : b.tagline;
          return <button key={id} type="button" onClick={() => {
            track('quick_pick', {
              intent: id,
              qty: b.qty,
              value: p.total
            });
            goToShop(id);
          }} className={`group relative grid grid-rows-[auto_auto_1fr_auto] border p-6 text-left transition-[border-color,background-color,transform] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ice active:translate-y-0 ${featured ? 'mf-hud border-marine bg-[#0D2440] hover:bg-[#10305A]' : 'border-white/15 bg-[#06121F] hover:border-marine/70'}`} data-id="element-358" data-mp="nffirczduvjh">
                <span className={`text-xs font-semibold uppercase tracking-[0.16em] ${featured ? 'text-marine' : 'text-titanium'}`} data-id="element-359" data-mp="vd7crndnca5i">{kicker}</span>
                <span className="mt-2 font-display text-2xl font-semibold uppercase tracking-[0.03em] text-white" data-id="element-360" data-mp="1528ubhzuazll">{bundleName(b, lang)}</span>
                <span className="mt-4" data-id="element-361" data-mp="10b0loo6c3i9h">
                  <span className="font-display text-4xl font-semibold text-white" data-id="element-362" data-mp="109xwqlsknpw7">{m(p.total)}</span>
                  <span className="mt-1 block text-sm text-titanium" data-id="element-363" data-mp="7n4prlol9udr">
                    {m(p.perUnit)} {es ? 'por luz' : 'per light'}
                  </span>
                </span>
                <span className="mt-6 flex items-center justify-between gap-3 border-t border-white/10 pt-4" data-id="element-364" data-mp="2j8dnhj5s4x1h">
                  <span className={`text-xs font-semibold uppercase tracking-[0.1em] ${p.savings > 0 ? 'text-ice' : 'text-titanium'}`} data-id="element-365" data-mp="2o301lu38x3p9">
                    {p.savings > 0 ? `${es ? 'Ahorras' : 'You save'} ${m(p.savings)}` : b.warranty === 'lifetime' ? es ? 'Garantía de por vida' : 'Lifetime warranty' : es ? 'Garantía de 1 año' : '1-year warranty'}
                  </span>
                  <span className="inline-flex items-center gap-1 font-display text-sm font-semibold uppercase tracking-[0.12em] text-white group-hover:text-ice" data-id="element-366" data-mp="36z9kcup3eifl">
                    {es ? 'Elegir' : 'Select'}
                    <ArrowDownRightIcon className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden data-id="element-367" data-mp="2x3y427y1d35q" />
                  </span>
                </span>
              </button>;
        })}
        </div>
      </div>
    </section>;
}